import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "locksmith-web-design-colorado",
  metaTitle: "Locksmith Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom locksmith web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Locksmith Web Design Colorado",
  eyebrow: "Locksmith Web Design — Colorado",
  h1: (
    <>
      Locksmith <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded locksmith websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Locksmith websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first locksmith sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Locksmith web design in Colorado",
    body: "Locksmith web design is the practice of building conversion-focused, mobile-first websites for locksmith businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — locksmith is a now-search from a phone in a parking lot." },
    { title: "Service pages per call type", body: "Lockout, rekey, automotive, commercial, smart lock, safe — each ranks independently." },
    { title: "24/7 click-to-call", body: "Sticky phone with after-hours messaging on every scroll." },
    { title: "Service area + ETA messaging", body: "Coverage map and estimated arrival times surfaced above the fold." },
    { title: "Trust + licensing badges", body: "State licensing, insurance, ALOA membership above the fold (locksmith fraud is rampant)." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Lockout is the highest-intent call there is", body: "Locked out of car/house — speed wins or loses. Period." },
    { heading: "Locksmith fraud poisons the market", body: "Verified local locksmiths with real reviews + licensing outrank scammers when the site is built right." },
    { heading: "Service-type pages drive ranking", body: "Automotive, commercial, and safe work each have unique search volume and pricing." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a locksmith website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a locksmith website?", a: "Typical timeline is 4–6 weeks for a 12–20 page locksmith site with full service and service-area pages. Larger multi-location locksmith businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs locksmith businesses?", a: "No — we build locksmith sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Locksmith Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for locksmith businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/locksmith-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
